/**
 * Tour content — design/Clinic Stock v2.dc.html:4254 (`tourSteps`).
 *
 * Two changes from the prototype's list, both forced by things that are true
 * here and were not there:
 *
 *   - **Steps can require a plan.** The prototype had no server-side gating, so
 *     its tour walked everyone through Targets. Here a Starter clinic taken to
 *     Targets lands on an upgrade wall in the middle of a tour, which turns a
 *     welcome into a sales pitch at the worst possible moment. Gated steps are
 *     dropped for plans that cannot reach them.
 *   - **No Learn step yet.** The prototype ends on the Learn page. That page is
 *     not built, so the tour ends on Account instead — a real page, and the one
 *     someone is most likely to want next.
 */
import type { Plan } from '@inventory-tracker/core';
import { canAccess } from '@inventory-tracker/core';
import type { Page } from '../Sidebar.js';

export interface TourStep {
  /** The page to navigate to before measuring. */
  page: Page;
  /** Matches a `data-tour="…"` attribute on a real element. */
  anchor: string;
  title: string;
  body: string;
  /** Plan-gated pages are skipped rather than shown as an upgrade wall. */
  requires?: 'analytics' | 'promotions' | 'targets';
}

export const QUICK_TOUR: TourStep[] = [
  {
    page: 'dashboard',
    anchor: 'kpis',
    title: 'Your daily overview',
    body: 'Total stock value, what needs reordering, and what expires soon. If you only look at one screen, this is it.',
  },
  {
    page: 'dashboard',
    anchor: 'addstock',
    title: 'Booking in a delivery',
    body: 'Add stock from any page. Enter the quantity, lot number and expiry date, and the batch is tracked from then on.',
  },
  {
    page: 'inventory',
    anchor: 'table',
    title: 'Everything you carry',
    body: 'Live quantities and batch status for every product, with one-tap “Log use” on each row. Click a row for its full history.',
  },
  {
    page: 'expiry',
    anchor: 'expiry',
    title: 'Getting ahead of expiry',
    body: 'Batches grouped by urgency. Stock is always used earliest-expiring-first, so this is what you act on before it becomes a write-off.',
  },
  {
    page: 'count',
    anchor: 'stocktake',
    title: 'Counting the shelf',
    body: 'Type what you can physically see and pick a reason for any difference. Adjustments go straight into the audit trail.',
  },
  {
    page: 'activity',
    anchor: 'movements',
    title: 'Every movement, with a reason',
    body: 'In, out, or written off, with who did it, when and why. Search a lot number to trace exactly where it went.',
  },
  {
    page: 'analytics',
    anchor: 'analytics',
    title: 'Where the money sits',
    body: 'Where the stock money went each month, how long each product will last, what is going out of date and what sits still.',
    requires: 'analytics',
  },
  {
    page: 'targets',
    anchor: 'monthbar',
    title: 'What the month needs to bring in',
    body: 'Your costs and profit goal become a number of treatments. The bar shows how close you are.',
    requires: 'targets',
  },
  {
    page: 'account',
    anchor: 'plancard',
    title: 'Your plan',
    body: 'Change plan, update payment details, or invite the rest of your team from here. That is the tour. Everything else is in the sidebar.',
  },
];

/**
 * Drops steps this plan cannot reach.
 *
 * Filtering rather than showing a locked step: a tour exists to make someone
 * feel oriented, and a wall halfway through does the opposite. They will meet
 * the upgrade prompt when they go looking for the feature, which is the moment
 * it is persuasive rather than intrusive.
 */
export function stepsForPlan(plan: Plan, steps: TourStep[] = QUICK_TOUR): TourStep[] {
  return steps.filter((s) => !s.requires || canAccess(plan, s.requires));
}
